import { OpenRouterModel } from "../services/openRouterModels";
import { ModelOption } from "../constants/models";

// ── Categories ──────────────────────────────────────────────────────────────

export type BrowserCategory =
  | "All"
  | "Favorites"
  | "Free"
  | "General Purpose"
  | "Coding"
  | "Creative Writing"
  | "Reasoning"
  | "Vision"
  | "Image Generation"
  | "Video Generation";

export const browserCategoryMeta: Record<
  BrowserCategory,
  { emoji: string; order: number }
> = {
  Favorites: { emoji: "❤️", order: 0 },
  All: { emoji: "🌐", order: 1 },
  Free: { emoji: "🆓", order: 2 },
  "General Purpose": { emoji: "⚡", order: 3 },
  Coding: { emoji: "💻", order: 4 },
  "Creative Writing": { emoji: "✍️", order: 5 },
  Reasoning: { emoji: "🧠", order: 6 },
  Vision: { emoji: "👁️", order: 7 },
  "Image Generation": { emoji: "🖼️", order: 8 },
  "Video Generation": { emoji: "🎬", order: 9 },
};

export function getOrderedBrowserCategories(): BrowserCategory[] {
  return (Object.keys(browserCategoryMeta) as BrowserCategory[]).sort(
    (a, b) => browserCategoryMeta[a].order - browserCategoryMeta[b].order,
  );
}

// ── Classification helpers ──────────────────────────────────────────────────

const CODING_RE =
  /code|coder|codex|codestral|devstral|starcoder|wizard-?code|phind/i;
const CREATIVE_RE =
  /rocinante|cydonia|skyfall|euryale|aion|mytho|creative|roleplay|storytell|fiction|minimax.*m2|midnight-?rose/i;
const REASONING_RE = /reason|thinking|\bo[134]\b|deepseek-r1|qwq|reflection/i;
const VIDEO_MODEL_RE =
  /\bveo\b|\bsora\b|\bseedance\b|\bwan[-.]?\d|\bwanvideo\b|video.?gen|gen.?video/i;

export function isFreeModel(m: OpenRouterModel): boolean {
  return (
    parseFloat(m.pricing.prompt) === 0 && parseFloat(m.pricing.completion) === 0
  );
}

export function hasVision(m: OpenRouterModel): boolean {
  return m.architecture?.input_modalities?.includes("image") ?? false;
}

export function hasImageGeneration(m: OpenRouterModel): boolean {
  return m.architecture?.output_modalities?.includes("image") ?? false;
}

export function hasVideoGeneration(m: OpenRouterModel): boolean {
  return (
    (m.architecture?.output_modalities?.includes("video") ?? false) ||
    VIDEO_MODEL_RE.test(`${m.id} ${m.name}`)
  );
}

export function categorizeModel(m: OpenRouterModel): BrowserCategory[] {
  const cats: BrowserCategory[] = ["All"];
  const text = `${m.id} ${m.name} ${m.description || ""}`;

  if (isFreeModel(m)) cats.push("Free");
  if (hasVision(m)) cats.push("Vision");
  if (hasImageGeneration(m)) cats.push("Image Generation");
  if (hasVideoGeneration(m)) cats.push("Video Generation");
  if (CODING_RE.test(text)) cats.push("Coding");
  if (CREATIVE_RE.test(text)) cats.push("Creative Writing");
  if (REASONING_RE.test(text)) cats.push("Reasoning");

  // Default to General Purpose if no specific category matched
  const specifics = cats.filter(
    (c) =>
      ![
        "All",
        "Favorites",
        "Free",
        "Vision",
        "Image Generation",
        "Video Generation",
      ].includes(c),
  );
  if (specifics.length === 0) cats.push("General Purpose");

  return cats;
}

// ── Display helpers ─────────────────────────────────────────────────────────

const PROVIDER_MAP: Record<string, string> = {
  openai: "OpenAI",
  anthropic: "Anthropic",
  google: "Google",
  "meta-llama": "Meta",
  mistralai: "Mistral AI",
  "x-ai": "xAI",
  deepseek: "DeepSeek",
  qwen: "Qwen",
  minimax: "MiniMax",
  cohere: "Cohere",
  ai21: "AI21",
  microsoft: "Microsoft",
  nvidia: "NVIDIA",
  thedrummer: "TheDrummer",
  perplexity: "Perplexity",
};

export function getProvider(modelId: string): string {
  const slash = modelId.indexOf("/");
  if (slash < 0) return modelId;
  const raw = modelId.substring(0, slash);
  return PROVIDER_MAP[raw] || raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function cleanModelName(name: string): string {
  // OpenRouter names like "OpenAI: GPT-4o" → "GPT-4o"
  return name.replace(/^[^:]+:\s*/, "");
}

export function formatPrice(pricePerToken: string): string {
  const val = parseFloat(pricePerToken);
  if (isNaN(val) || val === 0) return "Free";
  const perMillion = val * 1_000_000;
  if (perMillion < 0.01) return "<$0.01/M";
  return `$${perMillion.toFixed(2)}/M`;
}

export function formatContext(ctxLength: number): string {
  if (!ctxLength) return "—";
  if (ctxLength >= 1_000_000)
    return `${(ctxLength / 1_000_000).toFixed(ctxLength % 1_000_000 === 0 ? 0 : 1)}M`;
  if (ctxLength >= 1_000) return `${Math.round(ctxLength / 1_000)}K`;
  return `${ctxLength}`;
}

// ── Sorting ─────────────────────────────────────────────────────────────────

export type SortOption =
  | "recommended"
  | "price-asc"
  | "price-desc"
  | "context-desc"
  | "name-asc";

export function sortModels(
  models: OpenRouterModel[],
  sort: SortOption,
): OpenRouterModel[] {
  const sorted = [...models];
  switch (sort) {
    case "recommended":
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
    case "price-asc":
      return sorted.sort(
        (a, b) => parseFloat(a.pricing.prompt) - parseFloat(b.pricing.prompt),
      );
    case "price-desc":
      return sorted.sort(
        (a, b) => parseFloat(b.pricing.prompt) - parseFloat(a.pricing.prompt),
      );
    case "context-desc":
      return sorted.sort(
        (a, b) => (b.context_length || 0) - (a.context_length || 0),
      );
    case "name-asc":
      return sorted.sort((a, b) => a.name.localeCompare(b.name));
  }
}

// ── Search & filter ─────────────────────────────────────────────────────────

export function searchModels(
  models: OpenRouterModel[],
  query: string,
): OpenRouterModel[] {
  if (!query.trim()) return models;
  const q = query.toLowerCase();
  return models.filter((m) => {
    const text =
      `${m.id} ${m.name} ${m.description || ""} ${getProvider(m.id)}`.toLowerCase();
    return text.includes(q);
  });
}

export function filterByCategory(
  models: OpenRouterModel[],
  category: BrowserCategory,
): OpenRouterModel[] {
  if (category === "All") return models;
  return models.filter((m) => categorizeModel(m).includes(category));
}

// ── Backward-compat adapter ─────────────────────────────────────────────────

export function toModelOption(m: OpenRouterModel): ModelOption {
  return {
    value: m.id,
    label: cleanModelName(m.name),
    desc: (m.description || "").substring(0, 100),
    vision: hasVision(m),
    imageGeneration: hasImageGeneration(m),
    videoGeneration: hasVideoGeneration(m),
    category: "General Purpose",
  };
}
